/**
 * Onboarding Page — Individual auto-complete
 *
 * Self-signup users are always individuals. On mount this page:
 *   1. Calls completeOnboarding({ accountType: 'INDIVIDUAL' }) — backend provisions
 *      a personal org + FREE-plan ORGANIZATION license.
 *   2. Calls refreshUser() — pulls the freshly-minted entitlements from /auth/me.
 *   3. Navigates directly to /dashboard.
 *
 * Organization onboarding is platform-admin only and lives in the platform admin
 * portal — there is no self-service org path here.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BarChart3 } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useAuthStore } from '../../stores/authStore';
import authService from '../../services/api/authService';

/* ── Styled components ── */

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  display: flex;
  flex-direction: column;
`;

const SetupHeader = styled.div`
  background: ${({ theme }) => theme.colors.gradients.sidebar};
  color: ${({ theme }) => theme.colors.text.white};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  svg { width: 20px; height: 20px; }
`;

const SetupHeaderDivider = styled.span`
  opacity: 0.5;
  margin: 0 ${({ theme }) => theme.spacing[1]};
`;

const CenterContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const StatusText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ── Component ── */

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const isSuperAdmin = useAuthStore((state) => state.user?.isSuperAdmin ?? false);

  useEffect(() => {
    let cancelled = false;

    async function completeAndRedirect() {
      // Super admins are platform-level — never onboard or provision a license for them.
      if (isSuperAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // Provision the personal org + FREE-plan license on the backend.
      // Swallow errors — backend may return 409/400 if already complete.
      await authService.completeOnboarding({ accountType: 'INDIVIDUAL' }).catch(() => {});

      if (cancelled) return;

      // Pull fresh user data (onboardingComplete: true + entitlements).
      // refreshUser swallows its own errors internally — no outer catch needed.
      await refreshUser();

      if (!cancelled) {
        navigate('/dashboard', { replace: true });
      }
    }

    completeAndRedirect();

    return () => {
      cancelled = true;
    };
  }, [navigate, refreshUser]);

  return (
    <PageContainer>
      <SetupHeader>
        <BarChart3 />
        <span>Upskill ABA</span>
        <SetupHeaderDivider>&mdash;</SetupHeaderDivider>
        <span>Setting up your account…</span>
      </SetupHeader>

      <CenterContent>
        <LoadingSpinner size="lg" />
        <StatusText>Setting up your workspace…</StatusText>
      </CenterContent>
    </PageContainer>
  );
};

export default OnboardingPage;
